import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge } from '../../atoms';
import { useLawEdition } from '../useLawEdition';
import { type Matter, statusVariant } from '../pages/MattersIndex';

const muted = { color: 'var(--color-ink-3)' } as const;
const cell = {
  padding: 'var(--sp-2) var(--sp-4)',
  borderBottom: '1px solid var(--color-border)',
} as const;

/**
 * This client's litigation matters, read from the master-chart. Uses the SAME query key
 * as the Matters index + MatterDetail (`law-master-chart`), so across all three surfaces
 * it is one deduped fetch; we filter to the client here. Default-off while flags load.
 */
export function useClientMatters(clientId: string) {
  const { mattersOn } = useLawEdition();
  const q = useQuery({
    queryKey: ['law-master-chart'],
    queryFn: ({ signal }) => api.get<{ matters: Matter[] }>('/api/law/master-chart', signal),
    enabled: mattersOn && !!clientId,
    retry: false,
  });
  const matters = (q.data?.matters ?? []).filter((m) => m.client_id === clientId);
  return { mattersOn, matters, isLoading: q.isLoading };
}

/** The "Matters" tab panel on the client detail page — the primary law tab inside the
 *  existing hierarchy. Each matter links to its own tabbed MatterDetail. */
export function ClientMattersTab({ clientId }: { clientId: string }) {
  const { mattersOn, matters, isLoading } = useClientMatters(clientId);

  if (!mattersOn) return null;
  if (isLoading) return <div style={{ padding: 'var(--sp-4)', ...muted }}>Loading matters…</div>;
  if (matters.length === 0) {
    return <div style={{ padding: 'var(--sp-4)', ...muted }}>No matters for this client yet.</div>;
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r, 10px)', overflow: 'hidden', background: 'var(--color-surface, #fff)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm, 13px)' }}>
        <thead>
          <tr>
            {['Matter', 'Case #', 'Properties', 'Status'].map((h) => (
              <th scope="col" key={h} style={{ textAlign: 'left', ...cell, fontSize: 'var(--fs-xs)', textTransform: 'uppercase', letterSpacing: '0.04em', ...muted }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matters.map((m) => (
            <tr key={m.id}>
              <td style={cell}>
                <Link to={`/law/matters/${m.id}`} style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--brand-primary)', textDecoration: 'none' }}>{m.name}</Link>
              </td>
              <td style={{ ...cell, fontFamily: 'var(--font-mono)' }}>{m.case_no || '—'}</td>
              <td style={cell}>{m.property_count}</td>
              <td style={cell}><Badge variant={statusVariant(m.status)}>{m.status || '—'}</Badge></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
